/**
 * SimpleRole - Classe base abstrata para roles numéricas
 *
 * Para roles onde TODAS as variantes são do mesmo tipo (geralmente number).
 * Implementa os 4 pilares com lógica compartilhada via fatores de conversão.
 *
 * Roles que estendem: Area, Length, Mass, Volume, Speed, Pressure, Energy, etc.
 *
 * @example
 * class AreaRole extends SimpleRole<number> {
 *   readonly name = 'area';
 *   readonly base = 'square_meter';
 *   readonly factors = { square_meter: 1, hectare: 10000, acre: 4046.86 };
 *   readonly symbols = { square_meter: 'm²', hectare: 'ha', acre: 'ac' };
 * }
 */

import type {
  RoleSpec,
  VariantSpec,
  ValidationResult,
  ValidationRule,
  BaseFormatOptions,
  FormatSpec,
} from "../types";
import type { IRole, Result } from "./IRole";

// =============================================================================
// CONFIG TYPES
// =============================================================================

/**
 * Configuração de uma unidade em SimpleRole
 */
export interface SimpleUnitConfig {
  /** Fator de conversão para a base (ou função para temperatura) */
  factor?: number;
  /** Fórmula de conversão para base (para temperatura) */
  toBase?: (value: number) => number;
  /** Fórmula de conversão da base (para temperatura) */
  fromBase?: (value: number) => number;
  /** Símbolo da unidade (ex: "m²", "ha") */
  symbol: string;
  /** Nome singular (ex: "hectare") */
  singular?: string;
  /** Nome plural (ex: "hectares") */
  plural?: string;
  /** Se true, não adiciona espaço antes do símbolo (ex: 45° ao invés de 45 °) */
  noSpace?: boolean;
}

/**
 * Configuração de validação para SimpleRole
 */
export interface SimpleValidationConfig {
  /** Valor mínimo permitido */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Deve ser inteiro */
  integer?: boolean;
  /** Mensagem de erro customizada para min */
  minError?: string;
  /** Mensagem de erro customizada para max */
  maxError?: string;
}

/**
 * Alias para reconhecimento no cast
 */
export type UnitAliases = Record<string, string>;

// =============================================================================
// SIMPLE ROLE CLASS
// =============================================================================

/**
 * Classe base abstrata para roles numéricas simples.
 *
 * Subclasses devem definir:
 * - name: Nome da role
 * - base: Variante base
 * - units: Configuração de cada unidade
 * - aliases: (opcional) Aliases para cast
 * - validation: (opcional) Regras de validação
 */
export abstract class SimpleRole implements IRole<number> {
  // ===========================================================================
  // ABSTRACT PROPERTIES (subclasses devem implementar)
  // ===========================================================================

  /** Nome único da role */
  abstract readonly name: string;

  /** Nome da variante base */
  abstract readonly base: string;

  /** Configuração de cada unidade */
  abstract readonly units: Record<string, SimpleUnitConfig>;

  // ===========================================================================
  // OPTIONAL PROPERTIES (subclasses podem sobrescrever)
  // ===========================================================================

  /** Aliases para reconhecimento no cast (ex: "ha" → "hectare") */
  readonly aliases: UnitAliases = {};

  /** Regras de validação */
  readonly validation: SimpleValidationConfig = {};

  // ===========================================================================
  // INTROSPECTION
  // ===========================================================================

  getVariants(): string[] {
    return Object.keys(this.units);
  }

  hasVariant(variant: string): boolean {
    return variant in this.units;
  }

  // ===========================================================================
  // CONVERT (Pilar 1)
  // ===========================================================================

  convert<TFrom = number, TTo = number>(
    from: string,
    to: string,
    value: TFrom,
  ): TTo {
    const numValue = value as unknown as number;

    // Otimização: mesma variante
    if (from === to) {
      return value as unknown as TTo;
    }

    // Converte para base e depois para destino
    const baseValue = this.toBase(from, numValue);
    return this.fromBase(to, baseValue) as unknown as TTo;
  }

  tryConvert<TFrom = number, TTo = number>(
    from: string,
    to: string,
    value: TFrom,
  ): Result<TTo> {
    try {
      if (!this.hasVariant(from)) {
        return { ok: false, error: `Unknown variant: ${from}` };
      }
      if (!this.hasVariant(to)) {
        return { ok: false, error: `Unknown variant: ${to}` };
      }
      const result = this.convert<TFrom, TTo>(from, to, value);
      return { ok: true, value: result };
    } catch (err) {
      return { ok: false, error: String(err) };
    }
  }

  toBase<T = number>(variant: string, value: T): number {
    const numValue = value as unknown as number;
    const unit = this.units[variant];

    if (!unit) {
      throw new Error(`Unknown variant: ${variant}`);
    }

    // Usa fórmula se definida (temperatura), senão usa fator
    if (unit.toBase) {
      return unit.toBase(numValue);
    }

    return numValue * (unit.factor ?? 1);
  }

  fromBase<T = number>(variant: string, baseValue: number): T {
    const unit = this.units[variant];

    if (!unit) {
      throw new Error(`Unknown variant: ${variant}`);
    }

    // Usa fórmula se definida (temperatura), senão usa fator
    if (unit.fromBase) {
      return unit.fromBase(baseValue) as unknown as T;
    }

    return (baseValue / (unit.factor ?? 1)) as unknown as T;
  }

  // ===========================================================================
  // CAST (Pilar 2)
  // ===========================================================================

  cast<T = number>(variant: string, input: unknown): T | null {
    // Já é número
    if (typeof input === "number") {
      return Number.isFinite(input) ? (input as unknown as T) : null;
    }

    // String
    if (typeof input === "string") {
      const parsed = this.parseString(input, variant);
      return parsed !== null ? (parsed as unknown as T) : null;
    }

    return null;
  }

  tryCast<T = number>(variant: string, input: unknown): Result<T> {
    const result = this.cast<T>(variant, input);
    if (result === null) {
      return {
        ok: false,
        error: `Cannot cast "${String(input)}" to ${this.name}:${variant}`,
      };
    }
    return { ok: true, value: result };
  }

  /**
   * Parseia string para número, detectando unidade se presente.
   * Subclasses podem sobrescrever para lógica customizada.
   */
  protected parseString(input: string, targetVariant: string): number | null {
    const trimmed = input.trim().toLowerCase();

    // Extrai número e possível unidade
    const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
    if (!match) {
      return null;
    }

    let [, numStr, unitStr] = match;

    // Normaliza número
    const numValue = this.parseNumber(numStr);
    if (numValue === null) {
      return null;
    }

    // Se tem unidade no input, converte se necessário
    unitStr = unitStr.trim();
    if (unitStr) {
      const detectedUnit = this.detectUnit(unitStr);
      if (detectedUnit && detectedUnit !== targetVariant) {
        // Converte da unidade detectada para a target
        return this.convert(detectedUnit, targetVariant, numValue);
      }
    }

    return numValue;
  }

  /**
   * Parseia string numérica considerando formatos brasileiro e americano
   */
  protected parseNumber(numStr: string): number | null {
    // Remove espaços
    numStr = numStr.replace(/\s/g, "");

    const lastComma = numStr.lastIndexOf(",");
    const lastDot = numStr.lastIndexOf(".");
    const hasComma = lastComma !== -1;
    const hasDot = lastDot !== -1;

    if (hasComma && hasDot) {
      if (lastComma > lastDot) {
        // Brasileiro: 1.000,50
        numStr = numStr.replace(/\./g, "").replace(",", ".");
      } else {
        // Americano: 1,000.50
        numStr = numStr.replace(/,/g, "");
      }
    } else if (hasComma && !hasDot) {
      // Só vírgulas: detecta se é milhar ou decimal
      const afterComma = numStr.split(",").slice(1);
      const isThousandSep = afterComma.every((part) => part.length === 3);
      if (isThousandSep) {
        numStr = numStr.replace(/,/g, "");
      } else {
        numStr = numStr.replace(",", ".");
      }
    } else if (!hasComma && hasDot) {
      // Só pontos: múltiplos pontos = milhar brasileiro
      const afterDot = numStr.split(".").slice(1);
      if (afterDot.length > 1) {
        numStr = numStr.replace(/\./g, "");
      }
    }

    const value = parseFloat(numStr);
    return isNaN(value) ? null : value;
  }

  /**
   * Detecta unidade a partir de string (símbolo ou alias)
   */
  protected detectUnit(unitStr: string): string | null {
    const normalized = unitStr.toLowerCase().trim();

    // Checa aliases primeiro
    if (normalized in this.aliases) {
      return this.aliases[normalized];
    }

    // Checa símbolos e nomes
    for (const [variant, config] of Object.entries(this.units)) {
      if (
        config.symbol.toLowerCase() === normalized ||
        config.singular?.toLowerCase() === normalized ||
        config.plural?.toLowerCase() === normalized ||
        variant.toLowerCase() === normalized
      ) {
        return variant;
      }
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE (Pilar 3)
  // ===========================================================================

  validate(variant: string, value: unknown): ValidationResult {
    const errors: string[] = [];

    // Tipo
    if (typeof value !== "number") {
      return { valid: false, errors: ["Value must be a number"] };
    }

    // Finito
    if (!Number.isFinite(value)) {
      return { valid: false, errors: ["Value must be finite"] };
    }

    // Min
    if (this.validation.min !== undefined && value < this.validation.min) {
      const msg =
        this.validation.minError ??
        `Value must be at least ${this.validation.min}`;
      errors.push(msg);
    }

    // Max
    if (this.validation.max !== undefined && value > this.validation.max) {
      const msg =
        this.validation.maxError ??
        `Value must be at most ${this.validation.max}`;
      errors.push(msg);
    }

    // Integer
    if (this.validation.integer && !Number.isInteger(value)) {
      errors.push("Value must be an integer");
    }

    return { valid: errors.length === 0, errors };
  }

  isValid(variant: string, value: unknown): boolean {
    return this.validate(variant, value).valid;
  }

  // ===========================================================================
  // FORMAT (Pilar 4)
  // ===========================================================================

  format(variant: string, value: unknown, options: BaseFormatOptions = {}): string {
    const { decimals = 2, verbose = false, locale } = options;
    const unit = this.units[variant];

    if (!unit) {
      throw new Error(`Unknown variant: ${variant}`);
    }

    const numValue = value as number;

    // Formata número
    let formattedValue: string;
    if (locale) {
      formattedValue = numValue.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      });
    } else {
      formattedValue = Number(numValue.toFixed(decimals)).toString();
    }

    // Escolhe símbolo ou nome
    const separator = unit.noSpace ? "" : " ";

    if (verbose) {
      const name = numValue === 1 ? unit.singular : unit.plural;
      return `${formattedValue} ${name || unit.symbol}`;
    }

    return `${formattedValue}${separator}${unit.symbol}`;
  }

  // ===========================================================================
  // COMPATIBILITY
  // ===========================================================================

  toSpec(): RoleSpec<number> {
    const variants: Record<string, VariantSpec<unknown, number>> = {};

    for (const [name, config] of Object.entries(this.units)) {
      const factor = config.factor ?? 1;

      const toBaseFn = config.toBase
        ? (v: unknown) => config.toBase!(v as number)
        : (v: unknown) => (v as number) * factor;
      const fromBaseFn = config.fromBase
        ? (v: number) => config.fromBase!(v)
        : (v: number) => v / factor;

      variants[name] = {
        type: "number",
        toBase: toBaseFn,
        fromBase: fromBaseFn,
        cast: (input: unknown) => this.cast(name, input),
        validate: {
          min: this.validation.min,
          max: this.validation.max,
          integer: this.validation.integer,
          custom: (v: unknown) => {
            const result = this.validate(name, v);
            return result.valid ? null : result.errors[0];
          },
        } as ValidationRule,
        format: {
          symbol: config.symbol,
          singular: config.singular,
          plural: config.plural,
        } as FormatSpec<unknown>,
      };
    }

    return {
      base: this.base,
      variants,
    };
  }
}
